import { z } from "zod";

import { DEPARTMENT_LIST } from "../departments";

export const NAME_SOURCE = "https://www.insee.fr/fr/statistiques/8595130";
export const NAME_EDITION = 2025;
export const NAME_FIRST_YEAR = 1900;
export const NAME_LAST_YEAR = 2025;
export const NAME_YEARS = NAME_LAST_YEAR - NAME_FIRST_YEAR + 1;
/** Departmental counts, and so the map and its rates, start here. */
export const NAME_MAP_FROM = 2000;
export const NAME_TOP_SIZE = { national: 100, department: 30 } as const;

export type Department = { code: string; name: string; slug: string };
export const DEPARTMENTS_BY_SLUG = new Map<string, Department>(
  DEPARTMENT_LIST.map((department) => [department.slug, department]),
);
/** Public paths of the name pages: the server's sitemap and the web's links agree on them. */
export const namePath = {
  name: (slug: string) => `/prenoms/${slug}`,
  year: (year: number) => `/prenoms/annee/${year}`,
  department: (slug: string) => `/prenoms/departement/${slug}`,
  yearDepartment: (year: number, slug: string) => `/prenoms/annee/${year}/${slug}`,
};
/**
 * Only names with enough births are indexable. 26k near-identical generated pages read as
 * scaled content to Google, and the whole site dropped out of non-brand results three weeks
 * after they shipped (Oct 2026). At 5,000 births about 1,300
 * names remain, which still carried two thirds of the name pages' search impressions.
 * Year x department pages are never indexed for the same reason: thousands of tables that
 * differ by a few rows. The page and the sitemap both read this, so they cannot disagree.
 */
export const NAME_INDEX_MIN_TOTAL = 5000;
export const isNameIndexable = (name: { total: number }) => name.total >= NAME_INDEX_MIN_TOTAL;

export const DEPARTMENTS_BY_CODE = new Map<string, Department>(
  DEPARTMENT_LIST.map((department) => [department.code, department]),
);

export const sexSchema = z.enum(["girls", "boys"]);
export type NameSex = z.infer<typeof sexSchema>;

const yearSeries = z.array(z.number()).length(NAME_YEARS);
export const nameSummarySchema = z.object({
  slug: z.string(),
  name: z.string(),
  total: z.number(),
  boys: z.number(),
  girls: z.number(),
});
export const nameStatsSchema = nameSummarySchema.extend({
  years: yearSeries,
  boysYears: yearSeries,
  /** Rank among names of the majority sex, 0 when the name is unranked that year. */
  ranks: yearSeries,
  departments: z.array(z.object({ code: z.string(), count: z.number(), rate: z.number() })),
});
export const nameParamSchema = z.object({
  slug: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[a-z0-9-]+$/),
});
export type NameSummary = z.infer<typeof nameSummarySchema>;
export type NameStats = z.infer<typeof nameStatsSchema>;

export const namePageSchema = z.object({
  data: nameStatsSchema,
  /** Rank by total births among names of the majority sex. */
  overallRank: z.number(),
  /** Names whose curve over the years looks like this one. */
  related: z.array(nameSummarySchema),
});
export type NamePageResponse = z.infer<typeof namePageSchema>;

export const rankedNameSchema = z.object({
  slug: z.string(),
  name: z.string(),
  count: z.number(),
  rank: z.number(),
  /** Rank the year before, null when outside that year's published ranking. */
  previousRank: z.number().nullable(),
  /** Rank in France the same year, null when outside the national ranking. */
  nationalRank: z.number().nullable(),
});
export type RankedName = z.infer<typeof rankedNameSchema>;
export const leaderSchema = z.object({ slug: z.string(), name: z.string(), count: z.number() });
export type Leader = z.infer<typeof leaderSchema>;
const birthsSchema = z.object({ boys: z.number(), girls: z.number() });

export const yearParamSchema = z.object({
  year: z.coerce.number().int().min(NAME_FIRST_YEAR).max(NAME_LAST_YEAR),
});
export const departmentParamSchema = z.object({
  department: z.string().refine((slug) => DEPARTMENTS_BY_SLUG.has(slug)),
});
export const yearDepartmentParamSchema = yearParamSchema.extend(departmentParamSchema.shape);
export const departmentRefSchema = z.object({
  code: z.string(),
  name: z.string(),
  slug: z.string(),
});

export const risingNameSchema = z.object({
  slug: z.string(),
  name: z.string(),
  count: z.number(),
  previousCount: z.number(),
});
export type RisingName = z.infer<typeof risingNameSchema>;

export const yearPageSchema = z.object({
  year: z.number(),
  births: birthsSchema,
  girls: z.array(rankedNameSchema),
  boys: z.array(rankedNameSchema),
  /** Names that gained the most births over the previous year. */
  rising: z.array(risingNameSchema),
  /** Names given for the first time in the file, with at least a handful of births. */
  newcomers: z.array(leaderSchema),
  /** Number one of each department that year. */
  leaders: z.array(
    z.object({
      code: z.string(),
      girl: leaderSchema.nullable(),
      boy: leaderSchema.nullable(),
    }),
  ),
});
export type YearPage = z.infer<typeof yearPageSchema>;

export const yearsOverviewSchema = z.array(
  z.object({
    year: z.number(),
    births: birthsSchema,
    girl: leaderSchema.nullable(),
    boy: leaderSchema.nullable(),
  }),
);
export type YearsOverview = z.infer<typeof yearsOverviewSchema>;

export const departmentPageSchema = z.object({
  department: departmentRefSchema,
  /** Most given names since NAME_MAP_FROM, and over the whole period. */
  recent: z.object({ girls: z.array(leaderSchema), boys: z.array(leaderSchema) }),
  all: z.object({ girls: z.array(leaderSchema), boys: z.array(leaderSchema) }),
  leaders: z.array(
    z.object({ year: z.number(), girl: leaderSchema.nullable(), boy: leaderSchema.nullable() }),
  ),
});
export type DepartmentPage = z.infer<typeof departmentPageSchema>;

export const departmentsOverviewSchema = z.array(
  z.object({
    department: departmentRefSchema,
    girl: leaderSchema.nullable(),
    boy: leaderSchema.nullable(),
  }),
);
export type DepartmentsOverview = z.infer<typeof departmentsOverviewSchema>;

export const yearDepartmentPageSchema = z.object({
  year: z.number(),
  department: departmentRefSchema,
  births: birthsSchema,
  girls: z.array(rankedNameSchema),
  boys: z.array(rankedNameSchema),
});
export type YearDepartmentPage = z.infer<typeof yearDepartmentPageSchema>;

/** Window of the "recent" and "previous" counts of the trends page. */
export const TREND_WINDOW = 5;
export const trendEntrySchema = z.object({
  slug: z.string(),
  name: z.string(),
  total: z.number(),
  /** Births over the last TREND_WINDOW years. */
  recent: z.number(),
  /** Births over the TREND_WINDOW years before. */
  previous: z.number(),
  /** Share of girls among all births with the name, 0 to 100. */
  girlsShare: z.number(),
  years: yearSeries,
});
export type TrendEntry = z.infer<typeof trendEntrySchema>;
export const trendsPageSchema = z.object({
  rising: z.array(trendEntrySchema),
  falling: z.array(trendEntrySchema),
  /** Names almost entirely given since 2010. */
  recentNames: z.array(trendEntrySchema),
  /** Once very common, now given a few times a year. */
  classics: z.array(trendEntrySchema),
  rarest: z.array(trendEntrySchema),
  balanced: z.array(trendEntrySchema),
});
export type TrendsPage = z.infer<typeof trendsPageSchema>;
